/**
 * Validation utilities for Contact Us form
 * @module contact.validation
 */

import type { DropdownOption } from '../../components/common/Dropdown';
import { VALIDATION_PATTERNS, ERROR_MESSAGES, FIELD_LIMITS } from '../../constants/contact.constants';
import type { FieldValidationError, ContactFormField } from '../../types/contact.types';

/**
 * Validates an email address format
 * Uses length check before regex to prevent ReDoS attacks
 * Additional pre-checks for common invalid patterns ensure correctness
 * @param email - Email address to validate
 * @returns True if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email?.trim()) return false;
  
  // Validate length before regex to prevent ReDoS attacks
  if (email.length > FIELD_LIMITS.EMAIL_MAX_LENGTH) return false;
  
  // Pre-checks for common invalid patterns (fast string operations)
  if (email.startsWith('.') || email.endsWith('.')) return false;
  if (email.includes('..')) return false;
  if (email.includes('.@') || email.includes('@.')) return false;
  
  // Apply ReDoS-safe regex pattern
  return VALIDATION_PATTERNS.EMAIL.test(email);
};

/**
 * Validates phone area code (must be exactly 3 digits)
 * @param areaCode - Phone area code to validate
 * @returns True if valid or empty, false otherwise
 */
export const isValidPhoneAreaCode = (areaCode: string): boolean => {
  if (!areaCode) return true; // Optional field
  return areaCode.length === FIELD_LIMITS.PHONE_AREA_CODE_LENGTH;
};

/**
 * Validates phone number format with optional separator after 3rd digit.
 * Accepts: 4567890, 456-7890, 456.7890, 456 7890
 * @param phoneNumber - Phone number string to validate
 * @returns True if phone number is valid or empty (optional field), false otherwise
 * @example
 * isValidPhoneNumber('456-7890') // returns true
 * isValidPhoneNumber('4567890')  // returns true
 * isValidPhoneNumber('12345678') // returns false (8 digits)
 */
export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) return true; // Optional field
  
  // First check if it matches the expected pattern with optional separator
  if (!VALIDATION_PATTERNS.PHONE_NUMBER.test(phoneNumber)) {
    return false;
  }
  
  // Strip separators and verify it's exactly 7 digits
  const digitsOnly = phoneNumber.replace(VALIDATION_PATTERNS.PHONE_SEPARATOR, '');
  return VALIDATION_PATTERNS.PHONE_NUMBER_DIGITS_ONLY.test(digitsOnly);
};

/**
 * Validates a dropdown selection
 * @param option - Selected dropdown option
 * @returns True if a valid option is selected
 */
export const isValidDropdownSelection = (option: DropdownOption | undefined): boolean => {
  return Boolean(option?.value && option.value !== '');
};

/**
 * Validates a required text field
 * @param value - Field value to validate
 * @returns True if field has content
 */
export const isValidRequiredField = (value: string): boolean => {
  return Boolean(value?.trim());
};

/**
 * Sanitizes phone input by allowing only digits and valid separators.
 * Removes invalid characters, enforces max digit length, and normalizes separator placement.
 * Accepts dash (-), dot (.), or space as separator after 3rd digit.
 * @param value - Raw input value from user
 * @param maxLength - Maximum number of digits allowed (typically 7 for phone number)
 * @returns Sanitized string containing only valid digits and at most one separator in correct position
 * @example
 * sanitizePhoneInput('456-7890', 7)     // returns '456-7890'
 * sanitizePhoneInput('456abc7890', 7)   // returns '4567890' (removes letters)
 * sanitizePhoneInput('45678901234', 7)  // returns '4567890' (truncates to 7 digits)
 * sanitizePhoneInput('456-78-90', 7)    // returns '456-7890' (normalizes multiple separators)
 */
export const sanitizePhoneInput = (value: string, maxLength: number): string => {
  // Remove all characters except digits, dash, dot, and space
  let sanitized = value.replaceAll(/[^0-9\-. ]/g, '');
  
  // Extract only digits to check length
  const digitsOnly = sanitized.replaceAll(/\D/g, '');
  
  // Limit to maxLength digits
  if (digitsOnly.length > maxLength) {
    // Truncate to maxLength digits while preserving format
    const truncatedDigits = digitsOnly.slice(0, maxLength);
    // If there was a separator in the original, try to preserve it
    const separatorMatch = /[-. ]/.exec(sanitized);
    if (separatorMatch) {
      const separator = separatorMatch[0];
      sanitized = truncatedDigits.slice(0, 3) + separator + truncatedDigits.slice(3);
    } else {
      sanitized = truncatedDigits;
    }
  }
  
  // Ensure only one separator and it's in the right position (after 3rd digit)
  // Remove any separators that aren't between 3rd and 4th digit
  const parts = sanitized.split(/[-. ]/);
  if (parts.length > 2) {
    // Multiple separators - keep only the first valid one
    const allDigits = parts.join('');
    const separatorMatch = /[-. ]/.exec(sanitized);
    const separator = separatorMatch ? separatorMatch[0] : '';
    if (allDigits.length > 3) {
      sanitized = allDigits.slice(0, 3) + separator + allDigits.slice(3);
    } else {
      sanitized = allDigits;
    }
  }
  
  return sanitized;
};

/**
 * Validates the entire contact form
 * @param formState - Current form state
 * @returns Array of validation errors
 */
export interface ContactFormState {
  selectedRegion: DropdownOption;
  selectedCenter: DropdownOption;
  selectedTopic: DropdownOption;
  teamNameOrUbtId: string;
  email: string;
  phoneAreaCode: string;
  phoneNumber: string;
  comments: string;
}

export const validateContactForm = (formState: ContactFormState): FieldValidationError[] => {
  const errors: FieldValidationError[] = [];

  // Validate Region
  if (!isValidDropdownSelection(formState.selectedRegion)) {
    errors.push({
      field: 'region' as ContactFormField,
      message: ERROR_MESSAGES.REGION_REQUIRED,
    });
  }

  // Validate Center
  if (!isValidDropdownSelection(formState.selectedCenter)) {
    errors.push({
      field: 'center' as ContactFormField,
      message: ERROR_MESSAGES.CENTER_REQUIRED,
    });
  }

  // Validate Topic
  if (!isValidDropdownSelection(formState.selectedTopic)) {
    errors.push({
      field: 'topic' as ContactFormField,
      message: ERROR_MESSAGES.TOPIC_REQUIRED,
    });
  }

  // Validate Team Name / UBT ID
  if (!isValidRequiredField(formState.teamNameOrUbtId)) {
    errors.push({
      field: 'teamNameOrUbtId' as ContactFormField,
      message: ERROR_MESSAGES.TEAM_NAME_REQUIRED,
    });
  }

  // Validate Email
  if (!isValidRequiredField(formState.email)) {
    errors.push({
      field: 'email' as ContactFormField,
      message: ERROR_MESSAGES.EMAIL_REQUIRED,
    });
  } else if (!isValidEmail(formState.email)) {
    errors.push({
      field: 'email' as ContactFormField,
      message: ERROR_MESSAGES.EMAIL_INVALID,
    });
  }

  // Validate Phone Area Code (optional but must be valid if provided)
  if (formState.phoneAreaCode && !isValidPhoneAreaCode(formState.phoneAreaCode)) {
    errors.push({
      field: 'phoneAreaCode' as ContactFormField,
      message: ERROR_MESSAGES.PHONE_AREA_CODE_INVALID,
    });
  }

  // Validate Phone Number (optional but must be valid if provided)
  if (formState.phoneNumber && !isValidPhoneNumber(formState.phoneNumber)) {
    errors.push({
      field: 'phoneNumber' as ContactFormField,
      message: ERROR_MESSAGES.PHONE_NUMBER_INVALID,
    });
  }

  // Validate Comments
  if (!isValidRequiredField(formState.comments)) {
    errors.push({
      field: 'comments' as ContactFormField,
      message: ERROR_MESSAGES.COMMENTS_REQUIRED,
    });
  }

  return errors;
};
